import { Heading, Position, SvgIcon, TextComponent, Tooltip } from '@salecycle/ui-component-library';
import { PropsWithChildren } from 'react';
import styled, { useTheme } from 'styled-components';

/*
  Experimental Component:
  This is a wrapper around the existing tooltip, but it allows us to write a tooltip with a title and a body. This may get moved
  to UCL in the future.

  `TooltipProps` has been a direct copy from Tooltip as the props are not exported. We should expose these props in the future to 
  save us from having to re-write them. 
*/

interface TooltipProps {
  /** A simple title for the tooltip. */
  title?: string;
  /** Placement of the tooltip overlay. One of "left", "right" (default), "top", "bottom". */
  position?: Position;
  /** Choose action to trigger tooltip. One of "mouseenter" (default), "focus click" */
  trigger?: string;
  /** Custom HTML content for the tooltip (overrides title). */
  html?: any;
  /** Distance of the tooltip from the wrapped element in pixels. Defaults to 10. */
  distance?: number;
  /** Offsets the tooltip on its opposite axis. Defaults to 0. */
  offset?: number;
  /**
   * Prevents the tooltip from being positioned outside of it's container. Defaults to true.
   */
  preventOverflow?: boolean;
  /** Optional styling for the child wrapper div */
  wrapperStyle?: any;
}

interface InternalTooltipProps {
  content: string;
  title?: string;
  iconAriaLabel?: string;
  /** Optional style for the content area (e.g. whiteSpace: 'pre-line' for line breaks). */
  contentStyle?: React.CSSProperties;
}

export type TooltipWithTitleProps = PropsWithChildren<Omit<TooltipProps, 'title' | 'html'>> & InternalTooltipProps;

const Container = styled.div`
  display: flex;
  padding: ${({ theme }) => `${theme.designToken.spacing3} ${theme.designToken.spacing4}`};
  gap: ${({ theme }) => theme.designToken.spacing2};
`;

const ContentContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.designToken.spacing1};
`;

const InternalTooltip = ({ iconAriaLabel, title, content, contentStyle }: InternalTooltipProps) => {
  const theme = useTheme();
  return (
    <Container>
      {title && (
        <div>
          <SvgIcon size="24" aria-label={iconAriaLabel} name="circle-info" color={theme.designToken.primary700} />
        </div>
      )}

      <ContentContainer>
        {title && <Heading variation="h3">{title}</Heading>}
        <div style={contentStyle}>
          <TextComponent
            Type="Text"
            fontSize={theme.designToken.fontSize2}
            fontWeigth={theme.designToken.fontWeightsWhitney1}
            lineHeight={theme.designToken.lineHeights6}
            letterSpacing={theme.designToken.letterSpacing1}
            textDecoration={theme.designToken.textDecorationNone}
          >
            {content}
          </TextComponent>
        </div>
      </ContentContainer>
    </Container>
  );
};

export function TooltipWithTitle({ title, content, contentStyle, ...props }: TooltipWithTitleProps) {
  return <Tooltip {...props} html={<InternalTooltip title={title} content={content} contentStyle={contentStyle} />} />;
}
